/**
 * `useRemoteRoster()` — the React surface the roster editor renders against, and the
 * **only** roster controller. It builds a {@link RemoteRosterController} over a **real
 * browser `WebSocket`** to the server's single global roster room at `WS_ROSTER_PATH` —
 * optimistic local apply, last-write-wins reconcile to the server's `roster_change`
 * broadcast — and exposes the {@link UseRoster} contract the editor depends on.
 *
 * It is a **thin wrapper over {@link useRoomController}** — the generic owns the
 * whole controller↔React bridge (connect effect, status, ref, snapshot, actions);
 * this wrapper supplies the roster connection factory and layers the roster-only
 * `computed` view on top so the editor's `UseRoster` shape is complete.
 *
 * Beyond the `UseRoster` surface it also reports the live connection status and
 * the server-assigned actor id (the page surfaces both).
 */
import { useCallback, useMemo } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { RosterCommand, RosterState } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import { WS_ROSTER_PATH } from "@lancer-vtt/shared";
import {
  RemoteRosterController,
  type RosterConnection,
  type DispatchResult,
} from "./remoteRosterController";
import { roomWsUrl, startKeepalive, type ConnStatus } from "../roomWs";
import { useRoomController } from "../useRoomController";
import type { RosterUndoState } from "./rosterHistory";

/**
 * The single named shape the roster editor renders against — the contract
 * `useRemoteRoster` satisfies. Read surface (`loading`/`state`/`computed`/`undoState`) +
 * write surface (`dispatch`/`undo`/`redo`):
 *   loading            true until the welcome handshake seeds authoritative state
 *   state              the raw `RosterState` (source of truth), null until loaded
 *   computed           every pilot's computed view (grit + stat blocks), on read
 *   dispatch(cmd)      apply a roster command; returns { ok } | { ok:false, error }
 *                      so the UI can surface a reducer rejection (e.g. HASE over budget)
 *   undo(scope) / redo(scope)
 *                      per-actor, per-**scope** undo/redo: a window names its own
 *                      `mech:<id>`/`pilot:<id>` scope so its UNDO only walks back that
 *                      subject; the authoritative stacks live server-side
 *   undoState(scope)   accessor → { canUndo, canRedo, labels } for that scope, so each
 *                      editor's UNDO/REDO availability reflects only its own subject
 */
export type UseRoster = {
  loading: boolean;
  state: RosterState | null;
  computed: Record<string, ComputedPilot>;
  dispatch: (command: RosterCommand) => DispatchResult;
  undo: (scope?: string) => boolean;
  redo: (scope?: string) => boolean;
  undoState: (scope?: string) => RosterUndoState;
}

/**
 * Connection lifecycle the page surfaces — the unified command-room {@link ConnStatus}.
 * Kept as a re-exported alias so existing `RosterConnStatus` imports are unchanged.
 */
export type RosterConnStatus = ConnStatus;

/** Resolve the roster room ws URL from the browser location. */
export function rosterWsUrl(): string {
  return roomWsUrl(WS_ROSTER_PATH);
}

/**
 * The default real-`WebSocket` wiring — opens a browser `WebSocket` to `url`,
 * wraps it as a {@link RosterConnection}, constructs a {@link RemoteRosterController},
 * and pipes inbound messages into it. Exported so a test can exercise it against a
 * real backend — it is the exact code path the browser hook runs.
 *
 * `onStatus` is bridged from the socket lifecycle; the controller flips to
 * `connected` on `roster_welcome` (the hook bridges that separately).
 */
export function openRosterConnection(
  compendium: Compendium,
  target: { url: string; token: string },
  onStatus: (s: RosterConnStatus) => void,
): { controller: RemoteRosterController; close: () => void } {
  const ws = new WebSocket(target.url);
  // The connection carries both transport AND the identity token.
  const conn: RosterConnection = {
    send: (data) => ws.send(data),
    close: () => ws.close(),
    token: target.token,
  };
  const c = new RemoteRosterController(compendium, conn);
  ws.addEventListener("message", (ev) => c.onMessage(String(ev.data)));
  // Keep the path warm — an idle socket is silently evicted mid-route (see `startKeepalive`).
  let stopKeepalive: (() => void) | null = null;
  // The roster room is hello-gated: send `roster_hello { token }` on open; the server
  // verifies it, binds the socket to the verified slot, and replies `roster_welcome`.
  // No implicit join.
  ws.addEventListener("open", () => {
    onStatus("connecting");
    c.sendJoin();
    // A dead path (no inbound frames, or the browser going offline) is a drop now, not when TCP
    // finally gives up — see `startKeepalive`.
    stopKeepalive = startKeepalive(ws, { onDead: () => onStatus("disconnected") });
  });
  ws.addEventListener("close", () => {
    stopKeepalive?.();
    onStatus("disconnected");
  });
  ws.addEventListener("error", () => onStatus("disconnected"));
  return {
    controller: c,
    close: () => {
      stopKeepalive?.();
      ws.close();
    },
  };
}

/** The shared `UseRoster` surface plus the shared-mode-only connection telemetry. */
export type UseRemoteRoster = {
  /** Live ws connection status (connecting → connected once welcomed → disconnected). */
  connStatus: RosterConnStatus;
  /** Our server-assigned actor id, or null until `roster_welcome` arrives. */
  actorId: string | null;
} & UseRoster

export type UseRemoteRosterOptions = {
  /**
   * Override the ws URL (tests point this at an ephemeral backend). Defaults to
   * {@link rosterWsUrl} resolved from `window.location`.
   */
  url?: string;
  /**
   * The `/ws/game` reclaim token presented in `roster_hello`. The page reads it off the
   * app-wide session. Required for the default real-`WebSocket` path; an injected
   * `connect` factory supplies its own.
   */
  token?: string;
  /**
   * Inject a connection + controller factory (tests drive a real backend without
   * the browser `WebSocket` global). When omitted, a real `WebSocket` is opened.
   */
  connect?: (
    compendium: Compendium,
    onStatus: (s: RosterConnStatus) => void,
  ) => { controller: RemoteRosterController; close: () => void };
}

/**
 * Open (or reuse the injected) connection to the shared roster room and adapt the
 * `RemoteRosterController` into React. Same return shape as `useRoster`, plus
 * `connStatus`/`actorId`.
 */
export function useRemoteRoster(
  compendium: Compendium,
  options: UseRemoteRosterOptions = {},
): UseRemoteRoster {
  const { url, token, connect } = options;

  // The roster connection factory the generic hook drives (default real-`WebSocket`
  // via `openRosterConnection`, the exact path the round-trip test exercises). The
  // token is presented in `roster_hello` so the server binds to the verified slot.
  const factory = useCallback(
    (onStatus: (s: ConnStatus) => void) =>
      connect
        ? connect(compendium, onStatus)
        : openRosterConnection(compendium, { url: url ?? rosterWsUrl(), token: token ?? "" }, onStatus),
    [compendium, url, token, connect],
  );

  // Retention ON: a `/ws/roster` drop keeps the desktop, its windows and their drafts mounted
  // (`GmDesktop`/`PlayerLanding` blank only while `loading`, i.e. before the first welcome). The
  // drop stays visible and harmless because the room reports to the app-wide link pill and
  // `dispatch` refuses while not connected. See `useRoomController.ts`'s
  // `UseRoomControllerOptions` doc for the full two-mode contract.
  const room = useRoomController<RosterState, RosterCommand, RemoteRosterController>(
    factory,
    [compendium, url, token, connect],
    { notConnectedError: "roster room not connected", retainStateAcrossReconnect: true },
  );

  // Roster-only: recompute every pilot's derived view from the reconciled raw
  // state + the local compendium. Computed stats never cross the wire.
  const computed = useMemo(
    () => (room.controller && room.state ? room.controller.computeAll() : {}),
    [room.controller, room.state],
  );

  return {
    loading: room.loading,
    state: room.state,
    computed,
    dispatch: room.dispatch,
    undo: room.undo,
    redo: room.redo,
    undoState: room.undoState,
    connStatus: room.connStatus,
    actorId: room.actorId,
  };
}
